import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Assessment, CheckCircle, HelpOutline, Schedule } from '@mui/icons-material';
import { Bar, Doughnut } from 'react-chartjs-2';
import { format, startOfWeek } from 'date-fns';

import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import Panel from '../components/ui/Panel';
import StatCard from '../components/ui/StatCard';
import { barOptions, doughnutOptions } from '../theme/charts';
import { allocationPalette, categoryPalette, layout } from '../theme/tokens';

const hours = (value) => `${Number(value || 0).toFixed(1)}h`;
const defaultWeek = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');

const Allocation = () => {
  const [weekStart, setWeekStart] = useState(defaultWeek);
  const [squad, setSquad] = useState('All people');
  const [squads, setSquads] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/analytics/squads')
      .then(({ data }) => {
        const names = (data.squads || []).filter((item) => !item.placeholder).map((item) => item.name);
        setSquads(names);
        if (!names.includes('All people') && names.length) setSquad(names[0]);
      })
      .catch(() => setSquads([]));
  }, []);

  useEffect(() => {
    if (!squad) return;
    setLoading(true);
    setError(null);
    api.get('/allocation/weekly', {
      params: { weekStart, scope: 'team', squad, timezone: 'Australia/Melbourne' },
    })
      .then(({ data }) => setSnapshot(data))
      .catch((requestError) => {
        setSnapshot(null);
        setError(requestError.status === 404
          ? 'No allocation snapshot exists for this week yet. Source ingestion has not run.'
          : requestError.message);
      })
      .finally(() => setLoading(false));
  }, [weekStart, squad]);

  const allocationChart = useMemo(() => {
    const allocation = snapshot?.allocation || {};
    return {
      labels: ['Available capacity'],
      datasets: [
        ['Meetings', allocation.meetingsHours, allocationPalette.meetings],
        ['Focus', allocation.focusHours, allocationPalette.focus],
        ['Attributed work', allocation.attributedWorkHours, allocationPalette.attributed],
        ['Unattributed', allocation.unattributedHours, allocationPalette.unattributed],
      ].map(([label, value, color]) => ({ label, data: [value || 0], backgroundColor: color })),
    };
  }, [snapshot]);

  const investmentChart = useMemo(() => {
    const items = (snapshot?.investmentMix || []).filter((item) => item.hours > 0);
    return {
      labels: items.map((item) => item.category),
      datasets: [{
        data: items.map((item) => item.hours),
        backgroundColor: items.map((item) => (categoryPalette[item.category] || categoryPalette.Other).main),
        borderWidth: 0,
      }],
    };
  }, [snapshot]);

  const unavailable = snapshot?.coverage
    ? Object.entries(snapshot.coverage).filter(([, status]) => status !== 'complete')
    : [];

  return (
    <Box>
      <PageHeader
        title="Weekly allocation"
        subtitle="Available capacity, evidence-backed effort, and unexplained time."
      />

      <Panel dense sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Week starting"
            type="date"
            size="small"
            value={weekStart}
            onChange={(event) => setWeekStart(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            select
            label="Team"
            size="small"
            value={squad}
            onChange={(event) => setSquad(event.target.value)}
            sx={{ minWidth: 220 }}
          >
            {[...new Set([squad, ...squads])].filter(Boolean).map((name) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Panel>

      {error && <Alert severity="info" sx={{ mb: 3 }}>{error}</Alert>}
      {unavailable.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Partial evidence: {unavailable.map(([source, status]) => `${source} ${status}`).join(', ')}.
          Unattributed time is the measured residual, not an assumed 10%.
        </Alert>
      )}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard label="Available capacity" value={hours(snapshot?.capacity?.availableHours)} icon={<Schedule />} loading={loading} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard label="Explained hours" value={hours(snapshot?.allocation?.explainedHours)} icon={<CheckCircle />} loading={loading} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard label="Explained coverage" value={`${Number(snapshot?.allocation?.explainedPercent || 0).toFixed(1)}%`} icon={<Assessment />} loading={loading} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard label="Unattributed" value={hours(snapshot?.allocation?.unattributedHours)} icon={<HelpOutline />} loading={loading} />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Panel title="Available-time allocation" subtitle="Leave and holidays are deducted before this allocation." loading={loading} isEmpty={!snapshot} bodyHeight={layout.chartHeight}>
            <Bar data={allocationChart} options={barOptions({ stacked: true, legend: true, horizontal: true, yTitle: 'Hours' })} />
          </Panel>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Panel title="Capacity deductions" loading={loading} isEmpty={!snapshot} bodyHeight={layout.chartHeight}>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {[
                ['Nominal capacity', snapshot?.capacity?.nominalHours],
                ['Public holidays', snapshot?.capacity?.publicHolidayHours],
                ['Leave', snapshot?.capacity?.leaveHours],
                ['Available capacity', snapshot?.capacity?.availableHours],
              ].map(([label, value]) => (
                <Stack key={label} direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">{label}</Typography>
                  <Typography fontWeight={700}>{hours(value)}</Typography>
                </Stack>
              ))}
            </Stack>
          </Panel>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Panel title="Investment mix" subtitle="Project, Toil, Change, Unplanned and Learn." loading={loading} isEmpty={!snapshot || !investmentChart.labels.length} bodyHeight={layout.chartHeight}>
            <Doughnut data={investmentChart} options={doughnutOptions()} />
          </Panel>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Panel title="Evidence and provenance" loading={loading} isEmpty={!snapshot} bodyHeight={layout.chartHeight}>
            <Stack spacing={1.5}>
              <Typography variant="body2">Snapshot: {snapshot?.provenance?.snapshotId}</Typography>
              <Typography variant="body2">Generated: {snapshot?.provenance?.generatedAt ? new Date(snapshot.provenance.generatedAt).toLocaleString() : '—'}</Typography>
              <Typography variant="body2">Sources: {(snapshot?.provenance?.sources || []).join(', ') || 'None'}</Typography>
              <Typography variant="body2" color="text.secondary">
                Unattributed time is available capacity not explained by calendar or Jira evidence.
              </Typography>
            </Stack>
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Allocation;
